import * as React from "react";
import { StopwatchStartResult } from "../domain/types/stopwatch.types";
import { getDateKey } from "shared/utils/date";
import { readPersisted } from "../services/stopwatchStorage";
import {
  startStopwatch,
  pauseStopwatch,
  clearStopwatchSession,
} from "../services/stopwatchService";
import { useAccurateStopwatch } from "../hooks/useAccurateStopwatch";
import { useStopwatchWarnings } from "../hooks/useStopwatchWarnings";
import { useDocumentStopwatchTitle } from "../hooks/useDocumentStopwatchTitle";

const MAX_SECONDS_DEFAULT = 11 * 3600;

export function useStopwatchStateController(
  initialMaxSeconds = MAX_SECONDS_DEFAULT,
) {
  const { seconds, recalc } = useAccurateStopwatch();

  const [maxSeconds] = React.useState(initialMaxSeconds);
  const [projectAccumulatedSeconds, setProjectAccumulatedSeconds] =
    React.useState(0);
  const [isRunning, setIsRunning] = React.useState(false);
  const [sessionDateKey, setSessionDateKey] = React.useState(
    readPersisted().sessionDateKey,
  );

  const { isPreviousDaySession, stopwatchWarning } = useStopwatchWarnings({
    seconds,
    sessionDateKey,
  });

  const hasPausedSession = !isRunning && seconds > 0;

  useDocumentStopwatchTitle(seconds, projectAccumulatedSeconds);

  React.useEffect(() => {
    const p = readPersisted();

    setIsRunning(p.isRunning);
    setSessionDateKey(p.sessionDateKey);
  }, []);

  const start = React.useCallback((): StopwatchStartResult => {
    const result = startStopwatch(maxSeconds);

    if (!result.ok) return result;

    const today = getDateKey();

    setSessionDateKey(today);
    setIsRunning(true);
    recalc();

    return result;
  }, [maxSeconds, recalc]);

  const pause = React.useCallback(() => {
    pauseStopwatch();
    setIsRunning(false);
    recalc();
  }, [recalc]);

  const clearSession = React.useCallback(() => {
    const today = getDateKey();

    clearStopwatchSession();

    setSessionDateKey(today);
    setIsRunning(false);
    recalc();
  }, [recalc]);

  const commitSession = clearSession;
  const reset = clearSession;

  const value = React.useMemo(
    () => ({
      seconds,
      projectAccumulatedSeconds,
      setProjectAccumulatedSeconds,
      isRunning,
      hasPausedSession,
      sessionDateKey,
      isPreviousDaySession,
      stopwatchWarning,
      start,
      pause,
      commitSession,
      reset,
    }),
    [
      seconds,
      projectAccumulatedSeconds,
      isRunning,
      hasPausedSession,
      sessionDateKey,
      isPreviousDaySession,
      stopwatchWarning,
      start,
      pause,
      commitSession,
      reset,
    ],
  );

  return value;
}
